import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";
import { alpha } from "@mui/material/styles";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import colors from "../../../theme/colors";
import { useCachedLoanPipeline } from "../../../hooks/useCachedLoanPipeline";
import { PANEL_BTN_OUTLINE_SX } from "../panelButtonStyles";
import { suggestLoansForSubmission } from "../submissionLoanSuggestions";
import { LANES, submissionBorrowerName } from "../submissionHelpers";

/**
 * Pick an existing loan to attach a submission to. Ported from the lendmarq-los
 * applications queue: search the cached pipeline, select one loan, confirm.
 *
 * On top of the port, the dialog opens with a short list of SUGGESTED loans —
 * pipeline loans whose borrowers, subject properties or entity match this
 * submission (see submissionLoanSuggestions). They're shortcuts into the same
 * list below, each labelled with why it matched, not an automatic answer.
 *
 * The assign route keys on `loanNumber` (not dealId), so a pipeline row without
 * one can't be a target — those are filtered out rather than offered and failed.
 */
const AssignLoanDialog = ({
  open,
  target,
  submissionLabel,
  onClose,
  onConfirm,
  onCreateLoan,
  saving,
}) => {
  const { pipeline, isLoaded, ensure } = useCachedLoanPipeline();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const canCreateLoan =
    Boolean(onCreateLoan) && target?.__type === LANES.LOAN_APPLICATION;

  useEffect(() => {
    if (!open) return;
    ensure();
    setQuery("");
    setSelected(null);
  }, [open, ensure]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (Array.isArray(pipeline) ? pipeline : []).filter(
      (l) => l.loanNumber != null,
    );
    if (!q) return list.slice(0, 200);
    return list
      .filter((l) =>
        `${l.loanNumber || ""} ${l.loanName || ""} ${l.originator || ""} ${
          l.loanStatus || ""
        }`
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 200);
  }, [pipeline, query]);

  const keyOf = (l) => l._id || l.dealId || l.loanNumber;

  // Inferred from the cached pipeline — no request, so this costs nothing when
  // it finds nothing. Recomputed per open rather than per keystroke: the
  // suggestions are about the submission, not about what's been typed.
  const suggestions = useMemo(
    () => (open ? suggestLoansForSubmission(target, pipeline) : []),
    [open, target, pipeline],
  );

  const loanLine = (l) =>
    [`#${l.loanNumber}`, l.originator, l.loanStatus]
      .filter(Boolean)
      .join(" · ");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // The suggestions block made the dialog tall enough to clip on a short
      // viewport. Flexing the paper lets the loan list give up its height first,
      // so the footer buttons stay reachable.
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100% - 64px)",
        },
      }}
    >
      <Box sx={{ p: "20px 22px 0", flexShrink: 0 }}>
        <Flx between gap={2}>
          <Box sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{ fontSize: 17, fontWeight: 700, color: colors.slate900 }}
            >
              Assign to an existing loan
            </Txt>
            {target && (
              <Txt
                component="div"
                sx={{ fontSize: 13, color: colors.slate500, mt: "4px" }}
              >
                {submissionLabel} ·{" "}
                {submissionBorrowerName(target) || "Unnamed borrower"}
              </Txt>
            )}
          </Box>
          {/* Disabled rather than hidden while saving: the dialog is locked mid
              request, and a close control that vanishes reads as a glitch. */}
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            aria-label="Close"
            sx={{ color: colors.slate400, mt: "-4px", mr: "-6px" }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Flx>
      </Box>

      {suggestions.length > 0 && (
        <Box sx={{ p: "24px 22px 0", flexShrink: 0, pb: 2.5 }}>
          <Flx centerY gap={0.75} sx={{ mb: "8px" }}>
            <AutoAwesomeRoundedIcon
              sx={{ fontSize: 15, color: "primary.main" }}
            />
            <Txt
              component="div"
              sx={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: colors.slate500,
              }}
            >
              Suggested loans
            </Txt>
          </Flx>
          <Flx column gap={1}>
            {suggestions.map(({ loan, reasons }) => {
              const on = selected && keyOf(selected) === keyOf(loan);
              return (
                <Flx
                  key={`suggested-${keyOf(loan)}`}
                  centerY
                  gap={1.25}
                  onClick={() => setSelected(loan)}
                  sx={{
                    p: "10px 12px",
                    cursor: "pointer",
                    borderRadius: "9px",
                    border: (t) =>
                      `1px solid ${on ? t.palette.primary.main : colors.slate200}`,
                    background: (t) =>
                      on ? alpha(t.palette.primary.main, 0.07) : colors.slate50,
                    "&:hover": {
                      borderColor: (t) => t.palette.primary.main,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Txt
                      component="div"
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.slate900,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {loan.loanName || `Loan #${loan.loanNumber}`}
                    </Txt>
                    <Txt
                      component="div"
                      sx={{ fontSize: 11.5, color: colors.slate500 }}
                    >
                      {loanLine(loan)}
                    </Txt>
                    {/* Why this loan came up. Shown in full rather than
                        summarized — a suggestion the user can't check is one
                        they have to take on faith. */}
                    <Flx wrap gap={0.5} sx={{ mt: "6px" }}>
                      {reasons.map((r) => (
                        <Box
                          key={r}
                          component="span"
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            letterSpacing: ".02em",
                            color: "primary.main",
                            background: (t) =>
                              alpha(t.palette.primary.main, 0.09),
                            borderRadius: "5px",
                            px: "6px",
                            py: "2px",
                          }}
                        >
                          {r}
                        </Box>
                      ))}
                    </Flx>
                  </Box>
                  {on && (
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 18, color: "primary.main" }}
                    />
                  )}
                </Flx>
              );
            })}
          </Flx>

          {/* <Divider sx={{ borderColor: colors.slate200, mt: 3, mb: 1 }} /> */}
        </Box>
      )}
      <Box sx={{ p: "16px 22px 0", flexShrink: 0 }}>
        <Flx centerY gap={0.75} sx={{ mb: "8px" }}>
          <Txt
            component="div"
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              color: colors.slate500,
            }}
          >
            All Loans
          </Txt>
        </Flx>
        <Flx centerY gap={1}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search loan #, name, correspondent…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.slate400 }} />
                </InputAdornment>
              ),
            }}
          />
          {/* The escape hatch from this dialog: searched every loan and none of
              them is the one. Loan applications only — see StatusCell for why
              creating from a credit auth 500s. */}
          {canCreateLoan && (
            <Button
              variant="outlined"
              size="small"
              disabled={saving}
              startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
              onClick={onCreateLoan}
              sx={{ ...PANEL_BTN_OUTLINE_SX, flexShrink: 0 }}
            >
              Create Loan
            </Button>
          )}
        </Flx>
      </Box>

      <Box
        sx={{
          mt: "12px",
          mx: "22px",
          border: `1px solid ${colors.slate200}`,
          borderRadius: "9px",
          flex: "1 1 auto",
          minHeight: 140,
          maxHeight: 340,
          overflowY: "auto",
        }}
      >
        {!isLoaded ? (
          <Flx centerX centerY sx={{ py: 5 }}>
            <CircularProgress size={22} />
          </Flx>
        ) : filtered.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              color: colors.slate500,
              fontSize: 13,
            }}
          >
            {query.trim()
              ? `No loans match "${query}".`
              : "No loans available."}
          </Box>
        ) : (
          filtered.map((l) => {
            const on = selected && keyOf(selected) === keyOf(l);
            return (
              <Flx
                key={keyOf(l)}
                centerY
                gap={1.25}
                onClick={() => setSelected(l)}
                sx={{
                  p: "10px 14px",
                  cursor: "pointer",
                  borderBottom: `0.5px solid ${colors.slate200}`,
                  borderLeft: (t) =>
                    `3px solid ${on ? t.palette.primary.main : "transparent"}`,
                  background: (t) =>
                    on ? alpha(t.palette.primary.main, 0.07) : "transparent",
                  "&:hover": {
                    background: (t) =>
                      on ? alpha(t.palette.primary.main, 0.07) : colors.slate50,
                  },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Txt
                    component="div"
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: colors.slate900,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.loanName || `Loan #${l.loanNumber}`}
                  </Txt>
                  <Txt
                    component="div"
                    sx={{ fontSize: 11.5, color: colors.slate500 }}
                  >
                    {loanLine(l)}
                  </Txt>
                </Box>
                {on && (
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 18, color: "primary.main" }}
                  />
                )}
              </Flx>
            );
          })
        )}
      </Box>

      <Flx
        centerY
        gap={1.25}
        sx={{ justifyContent: "flex-end", p: "18px 22px 20px", flexShrink: 0 }}
      >
        <Button onClick={onClose} variant="text" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || saving}
          onClick={() => onConfirm(selected)}
        >
          {saving ? "Assigning…" : "Assign loan"}
        </Button>
      </Flx>
    </Dialog>
  );
};

export default AssignLoanDialog;
